import {
  Button,
  Col,
  Layout,
  Row,
  Table,
  Tabs,
  Dropdown,
  Menu,
  Modal,
  Form,
  Input,
  InputNumber,
  message,
} from "antd";
import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import { Check, ChevronDown, Pen, Trash } from "lucide-react";
import { postRequest } from "../../Utils/ApiCalls";
import {
  formatDate,
  formatNumber,
  handleExportToExcel,
  handleExportToPDF,
  navigateTo,
} from "../../Utils/Common";
import ConfirmAction from "../../Components/ConfirmAction/ConfirmAction";
import ReasonModal from "../../Components/CustomModals/ReasonModal";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import { useTableData } from "../../Utils/useTableData";

const { TabPane } = Tabs;

const Discount = () => {
  const [loading,        setLoading]        = useState("");
  const [activeTab,      setActiveTab]      = useState("pending");
  const [modalVisible,   setModalVisible]   = useState(false);
  const [modalType,      setModalType]      = useState<"edit" | "review" | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [showDisapproveModal, setShowDisapproveModal] = useState(false);
  const [disapproveRemarks, setDisapproveRemarks] = useState("");
  const [form]                              = Form.useForm();

  const {
    dataSource,
    dataSize,
    loading: tableLoading,
    filterConfig,
    setFilterConfig,
    refetch: fetchTableData,
    handleChangePage,
    handlePageSizeChange,
    fetchExport,
  } = useTableData<any>({
    endpoint: "discounts/get",
    initialFilters: { page_size: 10, status: "pending" },
    isFormData: true,
  });

  function onChangeTab(activeKey: any) {
    setActiveTab(activeKey);
    setFilterConfig((prev: any) => ({ ...prev, status: activeKey, page: 1 }));
  }

  function openActionModal(type: "edit" | "review", record: any) {
    setSelectedRecord(record);
    setModalType(type);
    setModalVisible(true);
    form.setFieldsValue({
      rwt_no:            record?.rwt_no            ?? "",
      remaining_balance: record?.remaining_balance ?? null,
      amount:            record?.amount            ?? null,
    });
  }

  function closeActionModal() {
    setModalVisible(false);
    setModalType(null);
    setSelectedRecord(null);
    form.resetFields();
  }

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const response = await postRequest(`discounts/update`, { ...values, discount_id: selectedRecord?.id }, true);

      if (response.success) {
        message.success(response.data.message);
        setTimeout(() => {
          navigateTo("/discount");
        }, 1500);
      } else {
        message.error(
          response?.data?.response?.data?.messages?.error_messages?.[0] ??
            "Failed to update discount."
        );
      }
    } catch (error) {
      message.error("Error loading data. Please try again.");
    } finally {
      setLoading("");
      closeActionModal();
    }
  };

  const handleReview = async (action: "approved" | "disapproved") => {
    try {
      const values = await form.getFieldsValue();
      const response = await postRequest(
        `discounts/change_status`,
        {
          ...values,
          action,
          discount_id: selectedRecord?.id,
          ...(action === "disapproved" ? { reason: disapproveRemarks } : {}),
        },
        true
      );

      if (response.success) {
        message.success(response.data.message);
        setShowDisapproveModal(false);
        setTimeout(() => {
          navigateTo("/discount");
        }, 1500);
      } else {
        message.error(
          response?.data?.response?.data?.messages?.error_messages?.[0] ??
            "Failed to update discount status."
        );
      }
    } catch (error) {
      message.error("Error loading data. Please try again.");
    } finally {
      setLoading("");
      closeActionModal();
    }
  };

  function getBaseColumns() {
    return [
      { 
        title: "RWT Number",      
        dataIndex: "rwt_no",          
        key: "rwt_no" 
      },
      { 
        title: "Amount",          
        dataIndex: "remaining_balance", 
        key: "remaining_balance",          
        align: "right" as any, 
        render: (v: any) => formatNumber(v) 
      },
      { 
        title: "Discount Amount", 
        dataIndex: "amount", 
        key: "amount", 
        align: "right" as any, 
        render: (v: any) => formatNumber(v) 
      },
      { 
        title: "Added by",        
        dataIndex: "added_by_name",   
        key: "added_by_name" 
      },
      { 
        title: "Added on",        
        dataIndex: "added_on",        
        key: "added_on",        
        render: (date: any) => formatDate(date, "MMM DD, YYYY h:mm A") 
      },
    ];
  }

  function getTableColumns() {
    return [
      ...getBaseColumns(),
      {
        title: "",
        key: "action",
        width: 10,
        render: (_: any, record: any) => {
          if (activeTab !== "pending") return null;
          return (
            <Dropdown
              overlay={
                <Menu>
                  <Menu.Item
                    key="edit"
                    icon={<Pen strokeWidth={1.5} size={15} />}
                    onClick={() => openActionModal("edit", record)}
                  >
                    Edit
                  </Menu.Item>
                  <Menu.Item
                    key="review"
                    icon={<Check strokeWidth={1.5} size={15} />}
                    onClick={() => openActionModal("review", record)}
                  >
                    Review
                  </Menu.Item>
                </Menu>
              }
              trigger={["click"]}
            >
              <Button>Actions <ChevronDown strokeWidth={0.5} /></Button>
            </Dropdown>
          );
        },
      },
    ];
  }

  async function exportToExcel() {
    setLoading("export");
    try {
      const { success, data } = await fetchExport();
      if (success) handleExportToExcel(data, getBaseColumns(), "Discounts");
      else message.error("Failed to fetch data for export.");
    } catch { message.error("Error exporting data."); }
    finally   { setLoading(""); }
  }

  async function exportToPDF() {
    setLoading("export");
    try {
      const { success, data } = await fetchExport();
      if (success) {
        handleExportToPDF(data, getBaseColumns(), "Discounts.pdf", { title: "List of Discounts", content: `Generated on ${new Date().toLocaleString()}` }, `Total Records: ${data.length}`);
      } else message.error("Failed to fetch data for PDF export.");
    } catch { message.error("Error exporting PDF."); }
    finally   { setLoading(""); }
  }

  const sharedTableProps = {
    dataSource,
    rowKey: "id",
    loading: tableLoading,
    size: "small" as any,
    bordered: true,
    pagination: {
      current:          filterConfig.page,
      pageSize:         filterConfig.page_size,
      total:            dataSize,
      showSizeChanger:  true,
      onChange:         (page: number) => handleChangePage(page),
      onShowSizeChange: (_: number, size: number) => handlePageSizeChange(size),
    },
  };

  return (
    <Layout className="page">
      <Helmet><title>Agua Lab ERP System</title></Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title="Discounts"
              withDateRangeFilter={false}
              withAddButton={true}
              addBtnText="Discount"
              onCreate={() => navigateTo("/discounts/add")}
              withExportDropdownButton={true}
              disableExportButton={dataSource?.length === 0}
              onExportToExcel={exportToExcel}
              onExportToPDF={exportToPDF}
              withSearchBtn={true}
              onSearch={(value: any) => setFilterConfig((prev: any) => ({ ...prev, search: value, page: 1 }))}
            />
          </Col>
          <Col span={24}>
            <div className="table-container overflow-auto">
              <div className="min-w-[800px]">
                <Tabs type="card" className="custom-tabs" activeKey={activeTab} onTabClick={onChangeTab}>
                  <TabPane tab="Pending"     key="pending">     <Table {...sharedTableProps} columns={getTableColumns()} /></TabPane>
                  <TabPane tab="Approved"    key="approved">    <Table {...sharedTableProps} columns={getTableColumns()} /></TabPane>
                  <TabPane tab="Disapproved" key="disapproved"> <Table {...sharedTableProps} columns={getTableColumns()} /></TabPane>
                </Tabs>
              </div>
            </div>
          </Col>
        </Row>
      </Layout>

      {/* ── ACTION MODAL ── */}
      <Modal
        open={modalVisible}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {modalType === "edit"   && <Pen   size={16} strokeWidth={1.5} />}
            {modalType === "review" && <Trash size={16} strokeWidth={1.5} />}
            <span>{modalType === "edit" ? "Edit Discount" : "Review Discount"}</span>
          </div>
        }
        onCancel={closeActionModal}
        width={480}
        destroyOnClose
        footer={[
          <Button key="close" onClick={closeActionModal}>Cancel</Button>,
          modalType === "edit" && (
            <Button key="save" type="primary" onClick={handleSave}>
              Save Changes
            </Button>
          ),
          modalType === "review" && (
            <Button
              key="disapprove"
              danger
              onClick={() => {
                setDisapproveRemarks("");
                setShowDisapproveModal(true);
              }}
            >
              Disapprove
            </Button>
          ),
          modalType === "review" && (
            <ConfirmAction
              key="approve"
              title="Are you sure you want to approve this discount?"
              onConfirm={() => handleReview("approved")}
              okText="Approve"
              cancelText="Cancel"
            >
              <Button type="primary">Approve</Button>
            </ConfirmAction>
          ),
        ]}
      >
        {/* Editable fields */}
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item
            label="RWT Number"
            name="rwt_no"
          >
            <Input placeholder="Enter RWT number" disabled={true} />
          </Form.Item>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                label="Amount"
                name="remaining_balance"
              >
                <InputNumber
                  placeholder="0.00"
                  min={0}
                  precision={2}
                  style={{ width: "100%" }}
                  disabled={true}
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(v) => v?.replace(/,/g, "") as any}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Discount Amount"
                name="amount"
                rules={[{ required: true, message: "Discount amount is required" }]}
              >
                <InputNumber
                  placeholder="0.00"
                  min={0}
                  precision={2}
                  style={{ width: "100%" }}
                  disabled={modalType === "review"}
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(v) => v?.replace(/,/g, "") as any}
                />
              </Form.Item>
            </Col>
          </Row>

          {modalType === "review" && (
            <Form.Item label="Remarks" name="remarks">
              <Input.TextArea rows={3} placeholder="Add your review remarks here..." />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <ReasonModal
        showModal={showDisapproveModal}
        onCancel={() => setShowDisapproveModal(false)}
        title="Please provide a reason for disapproval:"
        reasonValue={disapproveRemarks}
        onReasonChange={(e: any) => setDisapproveRemarks(e.target.value)}
        footer={[
          <Button key="cancel" onClick={() => setShowDisapproveModal(false)}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            disabled={!disapproveRemarks.trim()}
            onClick={() => handleReview("disapproved")}
          >
            Confirm Disapprove
          </Button>,
        ]}
      />

      <PersistentFooter />
    </Layout>
  );
};

export default Discount;